import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { getExpectedReceipts, createExpectedReceipt, updateExpectedReceipt, clearExpectedReceipt, deleteExpectedReceipt, getCompanies, getBanks } from '../api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Receipt, CheckCircle } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { format } from 'date-fns';

const ExpectedReceiptsPage = ({ user, onLogout }) => {
  const [receipts, setReceipts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [deletingReceipt, setDeletingReceipt] = useState(null);
  const [formData, setFormData] = useState({
    company_id: '',
    account_id: '',
    reference_number: '',
    amount: '',
    currency: 'INR',
    client_customer: '',
    expected_date: '',
    clearance_date: '',
    receipt_type: 'cheque'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [receiptsRes, companiesRes, banksRes] = await Promise.all([
        getExpectedReceipts(),
        getCompanies(),
        getBanks()
      ]);
      setReceipts(receiptsRes.data);
      setCompanies(companiesRes.data);
      setBanks(banksRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        expected_date: new Date(formData.expected_date).toISOString(),
        clearance_date: new Date(formData.clearance_date).toISOString()
      };

      if (editingReceipt) {
        await updateExpectedReceipt(editingReceipt.id, payload);
        toast.success('Receipt updated successfully');
      } else {
        await createExpectedReceipt(payload);
        toast.success('Receipt added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleClearReceipt = async (receipt) => {
    try {
      await clearExpectedReceipt(receipt.id);
      toast.success('Receipt cleared successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to clear receipt');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteExpectedReceipt(deletingReceipt.id);
      toast.success('Receipt deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingReceipt(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete receipt');
    }
  };

  const resetForm = () => {
    setFormData({
      company_id: '',
      account_id: '',
      reference_number: '',
      amount: '',
      currency: 'INR',
      client_customer: '',
      expected_date: '',
      clearance_date: '',
      receipt_type: 'cheque'
    });
    setEditingReceipt(null);
  };

  const openEditDialog = (receipt) => {
    setEditingReceipt(receipt);
    setFormData({
      company_id: receipt.company_id,
      account_id: receipt.account_id,
      reference_number: receipt.reference_number,
      amount: receipt.amount.toString(),
      currency: receipt.currency,
      client_customer: receipt.client_customer,
      expected_date: format(new Date(receipt.expected_date), 'yyyy-MM-dd'),
      clearance_date: format(new Date(receipt.clearance_date), 'yyyy-MM-dd'),
      receipt_type: receipt.receipt_type
    });
    setDialogOpen(true);
  };

  const getCompanyBanks = () => {
    return banks.filter(b => b.company_id === formData.company_id);
  };

  const getBankName = (accountId) => {
    const bank = banks.find(b => b.id === accountId);
    return bank?.account_name || 'Unknown';
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk' }} data-testid="receipts-page-title">
              Expected Receipts
            </h1>
            <p className="text-slate-600">Track incoming payments - cheques, SWIFT transfers, and wire transfers</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-receipt-button">
                <Plus className="w-4 h-4" />
                Add Receipt
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingReceipt ? 'Edit Receipt' : 'Add New Expected Receipt'}</DialogTitle>
                <DialogDescription>Enter expected receipt details</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Select
                    value={formData.company_id}
                    onValueChange={(value) => setFormData({ ...formData, company_id: value, account_id: '' })}
                    required
                  >
                    <SelectTrigger data-testid="receipt-company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bank Account</Label>
                  <Select
                    value={formData.account_id}
                    onValueChange={(value) => setFormData({ ...formData, account_id: value })}
                    required
                    disabled={!formData.company_id}
                  >
                    <SelectTrigger data-testid="receipt-bank-select">
                      <SelectValue placeholder="Select bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      {getCompanyBanks().map((bank) => (
                        <SelectItem key={bank.id} value={bank.id}>
                          {bank.account_name} - {bank.bank_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Receipt Type</Label>
                  <Select
                    value={formData.receipt_type}
                    onValueChange={(value) => setFormData({ ...formData, receipt_type: value })}
                  >
                    <SelectTrigger data-testid="receipt-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="swift">SWIFT Transfer</SelectItem>
                      <SelectItem value="wire_transfer">Wire Transfer</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Reference Number</Label>
                  <Input
                    data-testid="reference-number-input"
                    value={formData.reference_number}
                    onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                    placeholder="Cheque No. / SWIFT Reference"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client/Customer</Label>
                  <Input
                    data-testid="client-customer-input"
                    value={formData.client_customer}
                    onChange={(e) => setFormData({ ...formData, client_customer: e.target.value })}
                    placeholder="e.g., ABC Corp"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    data-testid="receipt-amount-input"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger data-testid="receipt-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Expected Date</Label>
                  <Input
                    data-testid="expected-date-input"
                    type="date"
                    value={formData.expected_date}
                    onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Clearance Date</Label>
                  <Input
                    data-testid="clearance-date-input"
                    type="date"
                    value={formData.clearance_date}
                    onChange={(e) => setFormData({ ...formData, clearance_date: e.target.value })}
                    required
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1" data-testid="save-receipt-button">
                    {editingReceipt ? 'Update' : 'Create'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Expected Receipts</CardTitle>
            <CardDescription>All expected incoming payments and their clearance status</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading...</div>
            ) : receipts.length === 0 ? (
              <div className="text-center py-12" data-testid="no-receipts-message">
                <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No expected receipts yet</p>
                <p className="text-sm text-slate-400 mt-1">Add your first expected receipt</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reference No.</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Bank Account</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Expected Date</TableHead>
                      <TableHead>Clearance Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receipts.map((receipt) => (
                      <TableRow key={receipt.id} data-testid={`receipt-row-${receipt.id}`}>
                        <TableCell className="font-mono">{receipt.reference_number}</TableCell>
                        <TableCell className="capitalize">
                          <Badge variant="outline">{receipt.receipt_type}</Badge>
                        </TableCell>
                        <TableCell>{receipt.client_customer}</TableCell>
                        <TableCell>{getBankName(receipt.account_id)}</TableCell>
                        <TableCell className="font-semibold">
                          {formatCurrency(receipt.amount, receipt.currency)}
                        </TableCell>
                        <TableCell>{format(new Date(receipt.expected_date), 'dd MMM yyyy')}</TableCell>
                        <TableCell>{format(new Date(receipt.clearance_date), 'dd MMM yyyy')}</TableCell>
                        <TableCell>
                          <Badge variant={receipt.status === 'cleared' ? 'default' : 'secondary'}>
                            {receipt.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {receipt.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleClearReceipt(receipt)}
                                data-testid={`clear-receipt-${receipt.id}`}
                                className="text-green-600"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(receipt)}
                              data-testid={`edit-receipt-${receipt.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setDeletingReceipt(receipt);
                                setDeleteDialogOpen(true);
                              }}
                              data-testid={`delete-receipt-${receipt.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete receipt <strong>{deletingReceipt?.reference_number}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default ExpectedReceiptsPage;